//---------------------------------------------------------
//  IMPORTS
//---------------------------------------------------------
import {
  getCurrentPosition as capacitorGetCurrentPosition,
  startLiveLocation,
  stopLiveLocation,
} from '../utils/capacitor-gps-sdk.js'

//---------------------------------------------------------
//  CONSTANTES LOCALSTORAGE & ETAT MEMOIRE
//---------------------------------------------------------
const LS_KEY_POS = '__lastPosition'
const LS_KEY_HEADING = '__userHeading'
const LS_KEY_LAST_LOCATION = '__rapidoo_last_location'

let inMemoryLocation = null
let isWarmingUp = false

export const DEFAULT_LOCATION = {
  lat: 6.37,
  lng: 2.39,
}

//---------------------------------------------------------
//  DÉTECTION CONTEXTE CAPACITOR
//---------------------------------------------------------
function isCapacitorContext() {
  return window.isCapacitorApp === true
}

//---------------------------------------------------------
//  SAUVEGARDE / CHARGEMENT LOCALSTORAGE & MEMOIRE
//---------------------------------------------------------
export function saveLastPosition(pos) {
  if (!pos || typeof pos.lat !== 'number' || typeof pos.lng !== 'number') return
  const payload = {
    lat: pos.lat,
    lng: pos.lng,
    accuracy: pos.accuracy ?? null,
    heading: pos.heading ?? 0,
    speed: pos.speed ?? null,
    timestamp: Date.now(),
  }
  inMemoryLocation = payload
  window.__lastPosition = { lat: payload.lat, lng: payload.lng }
  try {
    localStorage.setItem(LS_KEY_POS, JSON.stringify({ lat: payload.lat, lng: payload.lng }))
    localStorage.setItem(LS_KEY_LAST_LOCATION, JSON.stringify(payload))
  } catch (e) {
    console.warn('Impossible de sauvegarder lastPosition', e)
  }
}

export function loadLastPosition() {
  if (inMemoryLocation) return { lat: inMemoryLocation.lat, lng: inMemoryLocation.lng }
  try {
    const data = localStorage.getItem(LS_KEY_POS)
    return data ? JSON.parse(data) : null
  } catch (e) {
    return null
  }
}

export function getCachedLocation(maxAgeMs = null) {
  if (inMemoryLocation) {
    if (!maxAgeMs || Date.now() - (inMemoryLocation.timestamp || 0) < maxAgeMs) {
      return { ...inMemoryLocation, isCached: true }
    }
  }

  try {
    const raw = localStorage.getItem(LS_KEY_LAST_LOCATION)
    if (raw) {
      const parsed = JSON.parse(raw)
      if (parsed && typeof parsed.lat === 'number' && typeof parsed.lng === 'number') {
        inMemoryLocation = parsed
        if (!maxAgeMs || Date.now() - (parsed.timestamp || 0) < maxAgeMs) {
          return { ...parsed, isCached: true }
        }
      }
    }
  } catch (e) {
    console.warn('Erreur lecture cache position:', e)
  }

  const legacy = loadLastPosition()
  if (legacy) {
    return {
      lat: legacy.lat,
      lng: legacy.lng,
      heading: loadHeading(),
      speed: null,
      isCached: true,
      timestamp: 0,
    }
  }

  return null
}

function saveHeading(heading) {
  try {
    localStorage.setItem(LS_KEY_HEADING, String(heading))
  } catch (e) {}
}

function loadHeading() {
  const h = localStorage.getItem(LS_KEY_HEADING)
  return h ? Number(h) : 0
}

// Charger depuis localStorage dès le début
window.__lastPosition = loadLastPosition()
window.__userHeading = loadHeading()

//---------------------------------------------------------
//  WARMUP EN ARRIÈRE-PLAN
//---------------------------------------------------------
export async function warmupLocation() {
  if (isWarmingUp) return
  isWarmingUp = true

  try {
    if (isCapacitorContext()) {
      const pos = await capacitorGetCurrentPosition().catch(() => null)
      if (pos) {
        saveLastPosition(pos)
        return
      }
    }

    if (navigator.permissions && navigator.permissions.query) {
      try {
        const permission = await navigator.permissions.query({ name: 'geolocation' })
        if (permission.state !== 'granted') {
          isWarmingUp = false
          return
        }
      } catch {}
    }

    if (!navigator.geolocation) {
      isWarmingUp = false
      return
    }

    navigator.geolocation.getCurrentPosition(
      pos => {
        saveLastPosition({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          heading: pos.coords.heading,
          speed: pos.coords.speed,
        })
      },
      () => {},
      {
        enableHighAccuracy: true,
        timeout: 4000,
        maximumAge: 120000,
      },
    )
  } catch {
  } finally {
    isWarmingUp = false
  }
}

//---------------------------------------------------------
//  CALCUL DU HEADING
//---------------------------------------------------------
export function computeHeading(pos1, pos2) {
  const toRad = deg => (deg * Math.PI) / 180
  const toDeg = rad => (rad * 180) / Math.PI

  const dLng = toRad(pos2.lng - pos1.lng)
  const y = Math.sin(dLng) * Math.cos(toRad(pos2.lat))
  const x =
    Math.cos(toRad(pos1.lat)) * Math.sin(toRad(pos2.lat)) -
    Math.sin(toRad(pos1.lat)) * Math.cos(toRad(pos2.lat)) * Math.cos(dLng)

  return (toDeg(Math.atan2(y, x)) + 360) % 360
}

//---------------------------------------------------------
//  METTRE À JOUR LE HEADING CSS
//---------------------------------------------------------
export function updateUserHeading(heading) {
  document.documentElement.style.setProperty('--user-heading', `${heading}deg`)
  window.__userHeading = heading
  saveHeading(heading)
}

//---------------------------------------------------------
//  RÉCUPÉRATION DE POSITION
//---------------------------------------------------------

//---------------------------------------------------------
//  WRAPPER CAPACITOR
//---------------------------------------------------------
async function getCurrentPositionCapacitor(options) {
  try {
    const pos = await capacitorGetCurrentPosition(options)

    let finalHeading = pos.heading
    if (
      (finalHeading === null || finalHeading === undefined) &&
      window.__lastPosition
    ) {
      finalHeading = computeHeading(window.__lastPosition, {
        lat: pos.lat,
        lng: pos.lng,
      })
    }

    saveLastPosition({ ...pos, heading: finalHeading ?? 0 })
    updateUserHeading(finalHeading ?? 0)

    return {
      lat: pos.lat,
      lng: pos.lng,
      heading: finalHeading ?? 0,
      speed: pos.speed ?? null,
      accuracy: pos.accuracy ?? null,
    }
  } catch (err) {
    throw new Error(err?.message || 'Erreur de géolocalisation Capacitor')
  }
}

//---------------------------------------------------------
//  API PUBLIQUE
//---------------------------------------------------------
export async function getCurrentPosition({
  enableHighAccuracy = true,
  timeout = 2500,
  maximumAge = 60000,
} = {}) {
  // 1. contexte capacitor → utiliser postMessage
  if (isCapacitorContext()) {
    return getCurrentPositionCapacitor({
      enableHighAccuracy,
      timeout,
      maximumAge,
    })
  }

  // 2. fallback web → navigator.geolocation
  if (!navigator.geolocation) {
    return Promise.reject(
      new Error('Géolocalisation non supportée par ce navigateur'),
    )
  }

  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      pos => {
        const { latitude, longitude, heading, speed, accuracy } = pos.coords
        let finalHeading = heading

        if (
          (finalHeading === null || finalHeading === undefined) &&
          window.__lastPosition
        ) {
          finalHeading = computeHeading(window.__lastPosition, {
            lat: latitude,
            lng: longitude,
          })
        }

        saveLastPosition({
          lat: latitude,
          lng: longitude,
          accuracy,
          heading: finalHeading ?? 0,
          speed: speed ?? null,
        })

        updateUserHeading(finalHeading ?? 0)

        resolve({
          lat: latitude,
          lng: longitude,
          heading: finalHeading ?? 0,
          speed: speed ?? null,
          accuracy,
        })
      },
      err => {
        let message = 'Impossible de récupérer la position'
        if (err.code === 1)
          message = 'Autorisation refusée pour la géolocalisation'
        else if (err.code === 2) message = 'Position non disponible'
        else if (err.code === 3)
          message = 'Timeout expiré pour la géolocalisation'
        reject(new Error(message))
      },
      { enableHighAccuracy, timeout, maximumAge },
    )
  })
}

async function fetchFreshPositionInBackground(onProgressiveUpdate) {
  try {
    const fresh = await getCurrentPosition({
      enableHighAccuracy: true,
      timeout: 5000,
      maximumAge: 5000,
    })
    if (typeof onProgressiveUpdate === 'function') {
      onProgressiveUpdate(fresh)
    }
  } catch {}
}

//---------------------------------------------------------
//  FALLBACK INTELLIGENT ET PROGRESSIF
//---------------------------------------------------------
export async function getCurrentPositionWithFallback(options = {}) {
  const {
    preferCache = false,
    onProgressiveUpdate = null,
    maxCacheAge = 15 * 60 * 1000,
    timeout = 2500,
    ...customOptions
  } = options

  // 1. Si preferCache est demandé et qu'on a un cache récent, le retourner immédiatement (0ms)
  const cached = getCachedLocation(maxCacheAge)
  if (preferCache && cached) {
    fetchFreshPositionInBackground(onProgressiveUpdate)
    return cached
  }

  try {
    // Premier essai rapide haute précision (2500ms)
    return await getCurrentPosition({
      enableHighAccuracy: true,
      timeout: timeout,
      maximumAge: 60000,
      ...customOptions,
    })
  } catch (error) {
    console.warn('High accuracy failed, trying fast low accuracy/cache fallback...')

    // Si on a un cache disponible
    const fallbackCache = getCachedLocation(60 * 60 * 1000)
    if (fallbackCache && preferCache) {
      fetchFreshPositionInBackground(onProgressiveUpdate)
      return fallbackCache
    }

    try {
      // Deuxième essai rapide (2500ms) sans haute précision
      return await getCurrentPosition({
        enableHighAccuracy: false,
        timeout: 2500,
        maximumAge: 300000,
        ...customOptions,
      })
    } catch (fallbackError) {
      const last = getCachedLocation() || { ...DEFAULT_LOCATION, isDefault: true }
      console.warn('Fallback → dernière position persistée ou défaut:', last)
      return last
    }
  }
}

//---------------------------------------------------------
//  SUIVI TEMPS RÉEL
//---------------------------------------------------------
export function watchUserPosition(callback, options = {}) {
  // 1. contexte capacitor → utiliser startLiveLocation
  if (isCapacitorContext()) {
    startLiveLocation(pos => {
      let finalHeading = pos.heading

      // calcul du heading si non fourni
      if (
        (finalHeading === null || finalHeading === undefined) &&
        window.__lastPosition
      ) {
        finalHeading = computeHeading(window.__lastPosition, {
          lat: pos.lat,
          lng: pos.lng,
        })
      }

      // sauvegarde
      window.__lastPosition = { lat: pos.lat, lng: pos.lng }
      saveLastPosition(window.__lastPosition)

      updateUserHeading(finalHeading ?? 0)

      const positionData = {
        lat: pos.lat,
        lng: pos.lng,
        heading: finalHeading ?? 0,
        speed: pos.speed ?? null,
      }

      if (typeof callback === 'function') {
        callback(positionData)
      }
    })
    // id factice pour compatibilité avec l'api web
    return Symbol('capacitor-watch-id')
  }

  // 2. fallback web → navigator.geolocation
  if (!navigator.geolocation) {
    throw new Error('Géolocalisation non supportée par ce navigateur')
  }

  const { enableHighAccuracy = true, timeout = 30000, maximumAge = 0 } = options

  return navigator.geolocation.watchPosition(
    pos => {
      const { latitude, longitude, heading, speed } = pos.coords
      let finalHeading = heading

      if (
        (finalHeading === null || finalHeading === undefined) &&
        window.__lastPosition
      ) {
        finalHeading = computeHeading(window.__lastPosition, {
          lat: latitude,
          lng: longitude,
        })
      }

      window.__lastPosition = { lat: latitude, lng: longitude }
      saveLastPosition(window.__lastPosition)

      updateUserHeading(finalHeading ?? 0)

      const positionData = {
        lat: latitude,
        lng: longitude,
        heading: finalHeading ?? 0,
        speed: speed ?? null,
      }

      if (typeof callback === 'function') {
        callback(positionData)
      }
    },
    err => {
      console.warn('Erreur de géolocalisation (watch):', err.message || err)
    },
    { enableHighAccuracy, timeout, maximumAge },
  )
}

//---------------------------------------------------------
//  ARRÊTER LE SUIVI
//---------------------------------------------------------
export function stopWatchingUserPosition(watchId) {
  // 1. contexte capacitor → utiliser stopLiveLocation
  if (isCapacitorContext()) {
    stopLiveLocation()
    return
  }

  // 2. fallback web → navigator.geolocation.clearWatch
  if (watchId && navigator.geolocation) {
    navigator.geolocation.clearWatch(watchId)
  }
}
